import { useContext, useState } from "react";
import TabInfo from "./AddNewTabs/TabInfo";
import TabDetail from "./AddNewTabs/TabDetail";
import { serviceService } from "../../../../services/api/serviceService";
import { firebaseService } from "../../../../services/api/firebaseService";
import { AuthContext } from "../../../../contexts/AuthContext";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import Loading from "../../components/Loading";
import Modal from "../../components/Modal";
import ServicePriceTierEditor from "../../components/ServicePriceTierEditor";

const formatFormName = (formName) =>
  formName ? formName.replace(/-/g, "_") : "";
const formatFormNameOutput = (formName) =>
  formName ? formName.replace(/_/g, "-") : "";

const AddServicePopup = ({ isOpen, onClose, onSubmit, data = null }) => {
  const { token } = useContext(AuthContext);
  const { id } = useParams();
  const navigate = useNavigate();

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const [formData, setFormData] = useState(
    data
      ? {
        ...data,
        category: {
          value: data?.category?.categoryName,
          label: data?.category?.categoryName,
        },
        formName: {
          value: formatFormName(data?.formName),
          label: formatFormName(data?.formName),
        },
        procedure_id: {
          value: data?.procedure?._id || data?.procedure_id,
          label: data?.procedure?.name || data?.procedure_id,
        },
        priority: data?.priority || 0,
      }
      : {
        serviceCode: "",
        serviceName: "",
        category: null,
        image: null,
        status: "Đang hoạt động",
        description: "",
        notes: "",
        formName: null,
        procedure_id: null,
        price: "",
        priority: "",
        submissionTime: "",
        reviewTime: "",
      },
  );

  const [priceTiers, setPriceTiers] = useState(
    data?.priceTiers?.length
      ? data.priceTiers
      : [{ minQty: 1, maxQty: 1, price: 0 }],
  );

  const validateForm = () => {
    const newErrors = {};
    if (!formData.serviceCode) newErrors.serviceCode = "Mã dịch vụ là bắt buộc";
    if (!formData.serviceName)
      newErrors.serviceName = "Tên dịch vụ là bắt buộc";
    if (!formData.category) newErrors.category = "Loại dịch vụ là bắt buộc";
    if (!formData.formName) newErrors.formName = "Loại form là bắt buộc";
    if (!formData.procedure_id)
      newErrors.procedure_id = "Thủ tục hướng dẫn là bắt buộc";

    if (!priceTiers.length) newErrors.priceTiers = "Phải có ít nhất 1 mức giá";

    priceTiers.forEach((t, i) => {
      if (!t.minQty || t.price <= 0) {
        newErrors.priceTiers = `Khoảng ${i + 1} không hợp lệ`;
      }
    });

    setErrors(newErrors);
    return newErrors;
  };

  const extractFirebaseImageUrls = (htmlContent) => {
    if (!htmlContent || typeof htmlContent !== "string") return [];
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, "text/html");
    const images = doc.querySelectorAll("img");
    const firebaseUrls = [];
    images.forEach((img) => {
      const src = img.getAttribute("src");
      if (src && (src.startsWith("https://storage.googleapis.com/") || src.startsWith("https://firebasestorage.googleapis.com/"))) {
        firebaseUrls.push(src);
      }
    });
    return firebaseUrls;
  };

  const processImagesInContent = async (htmlContent) => {
    if (!htmlContent || typeof htmlContent !== "string" || htmlContent === "<p><br></p>") {
      return htmlContent || "";
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, "text/html");
    const images = doc.querySelectorAll("img");

    if (images.length === 0) return htmlContent;

    for (const img of images) {
      const src = img.getAttribute("src");

      if (!src || src.startsWith("https://storage.googleapis.com/") || src.startsWith("https://firebasestorage.googleapis.com/")) {
        continue;
      }

      if (src.startsWith("data:image/")) {
        try {
          const fetchRes = await fetch(src);
          const blob = await fetchRes.blob();
          const fileName = `service-editor-${Date.now()}.png`;
          const file = new File([blob], fileName, { type: blob.type });

          const uploadRes = await firebaseService.upload_file(token, file);
          const uploadedUrl = uploadRes?.url;

          if (uploadedUrl) {
            img.setAttribute("src", uploadedUrl);
          }
        } catch (error) {
          console.error("Error uploading image from content:", error);
        }
      }
    }

    return doc.body.innerHTML;
  };

  const handleSubmit = async () => {
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      console.log("Validation errors:", formErrors);
      toast.error(
        <div>
          <p className="font-bold mb-1">Vui lòng điền đầy đủ:</p>
          <ul className="list-disc pl-5 text-left">
            {Object.values(formErrors).map((err, idx) => (
              <li key={idx}>{err}</li>
            ))}
          </ul>
        </div>
      );
      return;
    }

    try {
      setIsLoading(true);

      const serviceId = data?._id || id;

      const processedDescription = await processImagesInContent(formData.description);
      const processedNotes = await processImagesInContent(formData.notes);

      let finalLogoUrl = data?.image?.url || data?.image || "";
      if (formData.image instanceof File) {
        const uploadRes = await firebaseService.upload_file(token, formData.image);
        finalLogoUrl = uploadRes?.url || "";
      } else if (typeof formData.image === "string") {
        finalLogoUrl = formData.image;
      } else if (formData.image?.url) {
        finalLogoUrl = formData.image.url;
      }

      let imagesToDelete = [];
      if (data) {
        const oldLogoUrl = data.image?.url || data.image || "";
        const oldContentImages = [
          ...extractFirebaseImageUrls(data.description),
          ...extractFirebaseImageUrls(data.notes)
        ];
        const newContentImages = [
          ...extractFirebaseImageUrls(processedDescription),
          ...extractFirebaseImageUrls(processedNotes)
        ];

        imagesToDelete = oldContentImages.filter(url => !newContentImages.includes(url));

        if (formData.image instanceof File && oldLogoUrl && typeof oldLogoUrl === "string" && oldLogoUrl.startsWith("http")) {
          imagesToDelete.push(oldLogoUrl);
        }
      }

      const submitPayload = {
        serviceCode: formData.serviceCode,
        serviceName: formData.serviceName,
        category: formData.category?.value || formData.category,
        status: formData.status,
        description: processedDescription,
        notes: processedNotes,
        price: formData.price || "",
        priceTiers,
        image: finalLogoUrl || null,
        procedure: formData.procedure_id?.value || formData.procedure_id,
        procedure_id: formData.procedure_id?.value || formData.procedure_id,
        formName: formData.formName?.value
          ? formatFormNameOutput(formData.formName.value)
          : formData.formName,
        priority: Number(formData.priority) || 0,
        submissionTime: formData.submissionTime || "",
        reviewTime: formData.reviewTime || "",
      };

      if (data) {
        const res = await serviceService.edit_service_json(serviceId, submitPayload, token);
        toast.success(res?.message || "Cập nhật dịch vụ thành công");
      } else {
        const res = await serviceService.create_service_json(submitPayload, token);
        toast.success(res?.message || "Tạo dịch vụ thành công");
      }

      for (const url of imagesToDelete) {
        try {
          await firebaseService.delete_file(token, url);
        } catch (err) {
          console.warn("Lỗi xóa file Firebase cũ:", url, err);
        }
      }

      onSubmit?.();
      onClose();
      navigate("/quan-ly-dich-vu");
    } catch (err) {
      console.error("Submit error:", err);
      toast.error(err?.response?.data?.message || err?.response?.data?.error || "Không thể kết nối server");
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: null }));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={data ? "Chỉnh sửa dịch vụ" : "Thêm dịch vụ"}
      maxWidth="60vw"
      showCancelButton
      cancelButtonText="Hủy"
      primaryButtonText={
        isLoading ? "Đang xử lý..." : data ? "Lưu thay đổi" : "Thêm mới"
      }
      primaryButtonOnClick={handleSubmit}
    >
      {isLoading ? (
        <div className="h-[400px] flex items-center justify-center">
          <Loading />
        </div>
      ) : (
        <div className="p-6 space-y-6 overflow-auto max-h-[70vh]">
          <TabInfo
            data={formData}
            onInputChange={handleInputChange}
            errors={errors}
          />

          <TabDetail data={formData} onInputChange={handleInputChange} />

          <div>
            <h3 className="text-lg font-semibold mb-3">
              Cấu hình giá theo số lượng
            </h3>

            <ServicePriceTierEditor
              tiers={priceTiers}
              onChange={setPriceTiers}
            />

            {errors.priceTiers && (
              <p className="text-sm text-red-500 mt-2">{errors.priceTiers}</p>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
};

export default AddServicePopup;
